import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import Cookies from "js-cookie";
import { useState, useEffect } from "react";


const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 4;

const HotelAdd_WPayment_Review = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { fetchDraft, hotelData, loading, } = useHotel();

  const rowLabelSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", width: isMobile ? "100%" : "160px", flexShrink: 0 };
  const rowValueSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const [billingAddress, setBillingAddress] = useState({
    streetAddress: "",
    unit: "",
    city: "",
    region: "",
    postalCode: "",
  });

  const [payment, setPayment] = useState({
    accountName: "",
    bankName: "",
    branchCode: "",
    accountNumber: "",
  });

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (hotelData.billingAddress) {
      setBillingAddress(hotelData.billingAddress);
    }
    if (hotelData.payment) {
      const savedAccountNumber = Cookies.get("hotelAccountNumber");
      setPayment({
        accountName: hotelData.payment.accountName || "",
        bankName: hotelData.payment.bankName || "",
        branchCode: hotelData.payment.branchCode || "",
        accountNumber: savedAccountNumber || "",
      });
    }
  }, [hotelData]);

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const ReviewRow = ({ labelKey, value, editTarget }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "flex-start",
        gap: isMobile ? 0.5 : 2,
        padding: "20px 0",
        borderBottom: "1px solid #E0E0E0",
      }}
    >
      <Typography sx={rowLabelSx}>{t(labelKey)}</Typography>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={rowValueSx}>{value}</Typography>
        <Button
          onClick={() => navigate(editTarget)}
          sx={{
            alignSelf: "flex-start",
            color: "#31BACD",
            textTransform: "none",
            fontSize: isMobile ? "13px" : "14px",
            textDecoration: "underline",
            minWidth: "auto",
            padding: 0,
          }}
        >
          {t('Edit')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 1 }}>
            {t('PaymentReviewTitle')}
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#666", mb: 3 }}>
            {t('PaymentReviewDesc')}
          </Typography>

          <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
            {t('ReviewBankDetailLabel')}
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ width: "100%" }}>
              <ReviewRow labelKey="AccountNameRowLabel" value={payment.accountName} editTarget={`/hotel/add/payment-1/${id}?edit=true`} />
              <ReviewRow labelKey="BankNameRowLabel" value={payment.bankName} editTarget={`/hotel/add/payment-1/${id}?edit=true`} />
              <ReviewRow labelKey="BranchCodeRowLabel" value={payment.branchCode} editTarget={`/hotel/add/payment-1/${id}?edit=true`} />
              <ReviewRow labelKey="AccountNumberRowLabel" value={payment.accountNumber} editTarget={`/hotel/add/payment-1/${id}?edit=true`} />
              <ReviewRow labelKey="AddressRowLabel" value={billingAddress.streetAddress} editTarget={`/hotel/add/payment-2/${id}?edit=true`} />
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={() => navigate("/hotel/onboarding")}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/payment-2/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/hotel/add/overview/" + id)}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_WPayment_Review;
